import React, { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const checks = useMemo(() => {
    const len = password.length >= 8;
    const upper = /[A-Z]/.test(password);
    const lower = /[a-z]/.test(password);
    const digit = /\d/.test(password);
    const special = /[^A-Za-z0-9]/.test(password);
    const match = password.length > 0 && password === confirm;
    const score = [len, upper, lower, digit, special].filter(Boolean).length;
    const label = score <= 2 ? 'Weak' : score === 3 ? 'Fair' : score === 4 ? 'Good' : 'Strong';
    const color = score <= 2 ? '#ef4444' : score === 3 ? '#f59e0b' : score === 4 ? '#10b981' : '#059669';
    return { len, upper, lower, digit, special, match, score, label, color };
  }, [password, confirm]);

  if (!supabase) {
    return (
      <div style={{ maxWidth: 420, margin: '40px auto', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <h2>Reset password</h2>
        <p>Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY, then redeploy.</p>
      </div>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!checks.len) { setMessage('Password must be at least 8 characters'); return; }
    if (!checks.match) { setMessage('Passwords do not match'); return; }
    setBusy(true);
    try {
      if (!supabase) { setMessage('Authentication is not configured.'); return; }
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setMessage('Password updated. Redirecting…');
      setTimeout(() => { window.location.replace('/login'); }, 600);
    } catch (err: any) {
      setMessage(err?.message || 'Failed to update password. Make sure you used the latest email link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 420, margin: '40px auto', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <h2 style={{ marginBottom: 12 }}>Set a new password</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>This page works from the reset link sent to your email.</p>
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 10 }}>
        <input type="password" placeholder="New password (min 8)" value={password} onChange={e => setPassword(e.target.value)} required style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6 }} />
        {/* Strength meter */}
        {password && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${(checks.score / 5) * 100}%`, height: '100%', background: checks.color, transition: 'width 120ms' }} />
              </div>
              <span style={{ fontSize: 12, color: checks.color }}>{checks.label}</span>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 2, fontSize: 12 }}>
              <li style={{ color: checks.len ? '#059669' : '#6b7280' }}>• At least 8 characters</li>
              <li style={{ color: checks.upper ? '#059669' : '#6b7280' }}>• Uppercase letter</li>
              <li style={{ color: checks.lower ? '#059669' : '#6b7280' }}>• Lowercase letter</li>
              <li style={{ color: checks.digit ? '#059669' : '#6b7280' }}>• Number</li>
              <li style={{ color: checks.special ? '#059669' : '#6b7280' }}>• Special character</li>
            </ul>
          </div>
        )}

        <input type="password" placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} required style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6 }} />
        {confirm && (
          <div style={{ fontSize: 12, color: checks.match ? '#059669' : '#b91c1c' }}>
            {checks.match ? 'Passwords match' : 'Passwords do not match'}
          </div>
        )}

        {/* Submission banner when weak */}
        {!busy && password && checks.score <= 2 && (
          <div style={{ background: '#FEF3C7', color: '#92400E', padding: '6px 10px', borderRadius: 6, fontSize: 13 }}>
            Consider a stronger password for better security.
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Updating…' : 'Update password'}</button>
        {message && <div style={{ color: '#374151' }}>{message}</div>}
      </form>
    </div>
  );
};

export default ResetPasswordPage;
